import { useTheme as useNextTheme } from 'next-themes'

export type Theme = 'dark' | 'light'

interface UseThemeReturn {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

/**
 * Typed wrapper around next-themes' `useTheme`.
 *
 * The native hook returns `theme: string | undefined` because next-themes is
 * SSR-aware and the value is unknown until hydration. We always render the
 * client app inside a `<ThemeProvider defaultTheme="dark">` so `undefined`
 * collapses to `'dark'` here, and consumers get a narrowed `Theme` union.
 *
 * `toggleTheme` is a convenience for the most common consumer (the theme
 * toggle in the user menu); for explicit choices, prefer `setTheme('light')`.
 */
export const useTheme = (): UseThemeReturn => {
  const { theme, setTheme: setNextTheme, resolvedTheme } = useNextTheme()
  // Prefer the explicit `theme` value; fall back to `resolvedTheme` which is
  // populated by next-themes once mounted; finally pin to 'dark' (the
  // provider default) so SSR / pre-hydration renders don't see undefined.
  const current: Theme = (theme ?? resolvedTheme ?? 'dark') as Theme

  return {
    theme: current,
    setTheme: (t: Theme) => setNextTheme(t),
    toggleTheme: () => setNextTheme(current === 'dark' ? 'light' : 'dark'),
  }
}
